import { useState, type FormEvent } from 'react';
import type { Expense } from '@stfors/shared-types';
import { Modal } from './Modal';
import { IconAction } from './IconAction';
import { DateInput } from './DateInput';
import { MoneyInput } from './MoneyInput';
import './RecurringExpenseModal.css';
import { ApiError } from '../api/client';
import {
  RECUR_OPTIONS,
  labelForRrule,
  useCreateRecurringExpense,
  useUpdateRecurringExpense,
  type RecurringExpenseTemplate,
} from '../api/queries/recurring-expenses';
import { Toggle } from './Toggle';

// Cadence choices only (drop the "Does not repeat" sentinel — a rule always repeats).
const CADENCES = RECUR_OPTIONS.filter((o) => o.rrule !== null) as { value: string; label: string; rrule: string }[];

type Props =
  | { mode: 'edit'; template: RecurringExpenseTemplate; onClose: () => void }
  | { mode: 'repeat'; expense: Expense; onClose: () => void };

// One modal, two jobs:
//  - 'repeat'  → seed a NEW rule from an existing expense (POST, skip_first:
//               the source expense already covers its own date).
//  - 'edit'    → change an existing rule (PATCH). Editing rrule/starts_at
//               re-anchors the schedule; the server recomputes next_due_at.
export function RecurringExpenseModal(props: Props): JSX.Element {
  const create = useCreateRecurringExpense();
  const update = useUpdateRecurringExpense();

  const seed = props.mode === 'edit' ? props.template : props.expense;
  const [name, setName] = useState(
    props.mode === 'edit'
      ? props.template.name
      : props.expense.vendor ?? props.expense.type,
  );
  const [rrule, setRrule] = useState(
    props.mode === 'edit' ? props.template.rrule : 'FREQ=MONTHLY',
  );
  const [startsAt, setStartsAt] = useState<number | null>(
    props.mode === 'edit' ? props.template.starts_at : props.expense.date,
  );
  const [endsAt, setEndsAt] = useState<number | null>(
    props.mode === 'edit' ? props.template.ends_at : null,
  );
  const [amount, setAmount] = useState(seed.amount);
  const [taxAmount, setTaxAmount] = useState(seed.tax_amount);
  const [reimbursable, setReimbursable] = useState(seed.reimbursable);
  const [notes, setNotes] = useState(seed.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  // A stored rrule that isn't one of our presets (legacy / hand-authored)
  // still needs to be selectable so an edit doesn't silently rewrite it.
  const rruleOptions = CADENCES.some((c) => c.rrule === rrule)
    ? CADENCES
    : [...CADENCES, { value: 'custom', label: labelForRrule(rrule), rrule }];

  const busy = create.isPending || update.isPending;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Give the repeat a name.'); return; }
    if (startsAt === null) { setError('Pick a start date.'); return; }
    if (endsAt !== null && endsAt < startsAt) {
      setError('End date must be on or after the start date.');
      return;
    }
    const amt = Number(amount);
    const tax = Number(taxAmount || '0');
    if (!Number.isFinite(amt) || amt <= 0) { setError('Enter a positive amount.'); return; }
    if (!Number.isFinite(tax) || tax < 0) { setError('Sales tax must be zero or more.'); return; }
    if (tax > amt) { setError('Sales tax cannot exceed the amount.'); return; }

    try {
      if (props.mode === 'edit') {
        await update.mutateAsync({
          id: props.template.id,
          name: name.trim(),
          rrule,
          starts_at: startsAt,
          ends_at: endsAt,
          amount: amt.toFixed(2),
          tax_amount: tax.toFixed(2),
          reimbursable,
          notes: notes.trim() || null,
        });
      } else {
        await create.mutateAsync({
          name: name.trim(),
          project_id: props.expense.project_id,
          type: props.expense.type,
          amount: amt.toFixed(2),
          tax_amount: tax.toFixed(2),
          vendor_id: props.expense.vendor_id,
          tax_category: props.expense.tax_category ?? undefined,
          reimbursable,
          notes: notes.trim() || undefined,
          rrule,
          starts_at: startsAt,
          ends_at: endsAt,
          skip_first: true,
        });
      }
      props.onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { rrule?: string; tax_amount?: string }; message?: string } | null;
        setError(body?.details?.rrule ?? body?.details?.tax_amount ?? body?.message ?? 'Validation failed.');
      } else if (err instanceof ApiError && err.status === 403) {
        setError("You don't have permission to change this.");
      } else {
        setError('Could not save.');
      }
    }
  };

  const title = props.mode === 'edit' ? 'Edit repeating expense' : 'Repeat this expense';
  const contextLine = props.mode === 'edit'
    ? `${props.template.type}${props.template.project_case_number ? ` · ${props.template.project_case_number}` : ' · General business'}`
    : `${props.expense.type}${props.expense.project_case_number ? ` · ${props.expense.project_case_number}` : ' · General business'}${props.expense.vendor ? ` · ${props.expense.vendor}` : ''}`;

  return (
    <Modal
      title={title}
      open
      onClose={props.onClose}
      busy={busy}
      footer={
        <>
          <IconAction action="cancel" onClick={props.onClose} disabled={busy} />
          {props.mode === 'edit' ? (
            <IconAction
              action="save"
              type="submit"
              form="recur-modal-form"
              disabled={busy}
              label={busy ? 'Saving…' : 'Save'}
            />
          ) : (
            <button type="submit" form="recur-modal-form" className="btn-cyan-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Create repeat'}
            </button>
          )}
        </>
      }
    >
      <form id="recur-modal-form" className="rem-form" onSubmit={onSubmit}>
        <p className="rem-context">{contextLine}</p>

        <label className="rem-field">
          <span>Name</span>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <div className="rem-row">
          <label className="rem-field">
            <span>Frequency</span>
            <select className="form-input" value={rrule} onChange={(e) => setRrule(e.target.value)}>
              {rruleOptions.map((o) => <option key={o.value} value={o.rrule}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <div className="rem-row">
          <label className="rem-field">
            <span>Start date</span>
            <DateInput value={startsAt} onChange={setStartsAt} required />
          </label>
          <label className="rem-field">
            <span>End date (optional)</span>
            <DateInput value={endsAt} onChange={setEndsAt} />
          </label>
        </div>

        <div className="rem-row">
          <label className="rem-field">
            <span>Amount (USD)</span>
            <MoneyInput value={amount} onChange={setAmount} required />
          </label>
          <label className="rem-field">
            <span>Sales tax (USD)</span>
            <MoneyInput value={taxAmount} onChange={setTaxAmount} />
          </label>
        </div>

        <Toggle
          checked={reimbursable}
          onChange={setReimbursable}
          label="Reimbursable to client"
          className="rem-field rem-check"
        />

        <label className="rem-field">
          <span>Notes</span>
          <textarea className="form-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={10000} />
        </label>

        <p className="rem-hint">
          {props.mode === 'repeat'
            ? 'Any periods between the start date and today (respecting an end date) are created as pre-approved expenses automatically — except this expense you already recorded. Future periods generate on schedule.'
            : 'Schedule changes recompute the next due date. Past expenses already created are not re-generated.'}
        </p>
        {error && <p className="rem-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
